import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { curriculumApi } from '../../services/sessions/curriculumApi';

interface CopyCurriculumDialogProps {
  open: boolean;
  targetSessionId: string;
  onClose: () => void;
  onCopied: () => void;
}

const CopyCurriculumDialog: React.FC<CopyCurriculumDialogProps> = ({
  open, targetSessionId, onClose, onCopied
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sourceId, setSourceId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await sessionsApi.list({ includeArchived: true });
        if (cancelled) return;
        setSessions(all.filter(s => s._id !== targetSessionId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, targetSessionId]);

  const submit = async () => {
    if (!sourceId) return;
    setSubmitting(true);
    try {
      const result = await curriculumApi.copyFromSession({
        sourceSessionId: sourceId,
        targetSessionId
      });
      const { created = 0, skipped = 0, unmatched = 0 } = result || {};
      const parts: string[] = [];
      if (created)   parts.push(`${created} added`);
      if (skipped)   parts.push(`${skipped} skipped (already there)`);
      if (unmatched) parts.push(`${unmatched} unmatched section${unmatched === 1 ? '' : 's'}`);
      enqueueSnackbar(
        created > 0
          ? `Curriculum copied — ${parts.join(' · ')}`
          : `Nothing copied — ${parts.join(' · ') || 'no changes'}`,
        { variant: created > 0 ? 'success' : 'info' }
      );
      onCopied();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not copy curriculum', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <ContentCopy color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Copy curriculum from another session</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Copies subject assignments per class. Section-specific subjects are remapped by section name —
          unmatched sections are reported and skipped.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={24} /></Stack>
        ) : sessions.length === 0 ? (
          <Alert severity="info" variant="outlined">No other sessions yet to copy from.</Alert>
        ) : (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Source session</InputLabel>
              <Select
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                label="Source session"
              >
                {sessions.map(s => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      · {format(new Date(s.startDate), 'MMM yyyy')}–{format(new Date(s.endDate), 'MMM yyyy')}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" variant="outlined">
              Existing assignments in this session are kept — copy is additive.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          disableElevation
          disabled={!sourceId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Copying…' : 'Copy curriculum'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyCurriculumDialog;
